import type { LucideIcon } from "lucide-react";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * One way of identifying at the kiosk (card, name search, first visit).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const mode: IdentificationModeTab<"card"> = { id: "card", icon: CreditCard, label: "Mit Karte", testId: "patient-id-mode-card" };
 * ```
 */
export interface IdentificationModeTab<Mode extends string> {
  readonly id: Mode;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly testId: string;
}

/**
 * The identification mode switch shared by the scheduler and the prescription
 * flow (KO-07a): one tab per way of identifying, 56px tall, selection exposed
 * as `role="tab"` + `aria-selected` (FC-08, FC-27), and unselected tabs drawn
 * with a visible border and dark text instead of a washed-out tint (KO-22).
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <IdentificationModeTabs
 *   label={t("self-service.identification.mode-label")}
 *   modes={modes}
 *   activeMode="card"
 *   onModeChange={setMode}
 * />
 * ```
 */
export function IdentificationModeTabs<Mode extends string>({
  label,
  modes,
  activeMode,
  onModeChange,
}: {
  /** Accessible name of the tab list. */
  readonly label: string;
  readonly modes: ReadonlyArray<IdentificationModeTab<Mode>>;
  readonly activeMode: Mode;
  readonly onModeChange: (mode: Mode) => void;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex min-w-0 flex-wrap gap-3">
      {modes.map(({ id, icon: Icon, label: modeLabel, testId }) => {
        const selected = activeMode === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={testId}
            onClick={() => onModeChange(id)}
            className={cn(
              "flex min-h-14 min-w-[10rem] flex-1 items-center justify-center gap-3 rounded-2xl border-2 px-5 py-3 text-lg font-semibold transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45",
              selected
                ? "border-primary bg-primary-subtle text-primary-ink"
                : "border-border bg-card text-foreground hover:bg-foreground-soft",
            )}
          >
            <Icon aria-hidden className="size-6 shrink-0" />
            {modeLabel}
          </button>
        );
      })}
    </div>
  );
}
